import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { TrendingUp, TrendingDown, Activity } from "lucide-react";
import { SectionHeader, Stat, Empty } from "@/components/ui-bits";
import { getInsights } from "@/lib/api";
import { formatBRL, monthFullName, monthName } from "@/lib/format";

export default function Insights() {
  const [data, setData] = useState(null);

  useEffect(() => {
    getInsights().then(setData).catch(() => setData({}));
  }, []);

  const chartData = useMemo(
    () => (data?.months || []).map((m) => ({
      name: `${monthName(m.month_num)}/${String(m.year).slice(2)}`,
      total: m.total,
    })),
    [data]
  );

  const maxBody = (data?.body_parts?.[0]?.count) || 1;

  if (!data) return <p className="text-[#5C6658]">Carregando…</p>;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      <SectionHeader
        eyebrow="Análise"
        title="Insights"
        description="Inteligência do seu estúdio — quando você fatura mais e onde a agulha mais visita."
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="elegant-card p-7">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-[#7B8B6F]" />
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#5C6658]">Mês mais forte</p>
          </div>
          {data.best_month ? (
            <>
              <p className="font-serif-display text-3xl text-[#2E332C]">{formatBRL(data.best_month.total)}</p>
              <p className="text-sm text-[#7B8B6F] mt-1">{monthFullName(data.best_month.month_num)} / {data.best_month.year}</p>
            </>
          ) : <p className="text-sm text-[#5C6658]">Sem dados ainda.</p>}
        </div>
        <div className="elegant-card p-7">
          <div className="flex items-center gap-2 mb-3">
            <TrendingDown className="w-4 h-4 text-[#B86B6B]" />
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#5C6658]">Mês mais fraco</p>
          </div>
          {data.worst_month ? (
            <>
              <p className="font-serif-display text-3xl text-[#2E332C]">{formatBRL(data.worst_month.total)}</p>
              <p className="text-sm text-[#B86B6B] mt-1">{monthFullName(data.worst_month.month_num)} / {data.worst_month.year}</p>
            </>
          ) : <p className="text-sm text-[#5C6658]">Sem dados ainda.</p>}
        </div>
        <div className="elegant-card p-7">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="w-4 h-4 text-[#C4A77D]" />
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#5C6658]">Média mensal</p>
          </div>
          <p className="font-serif-display text-3xl text-[#C4A77D]">{formatBRL(data.average_month)}</p>
          <p className="text-sm text-[#5C6658] mt-1">Últimos 12 meses (com faturamento)</p>
        </div>
      </div>

      <div className="elegant-card p-8 mb-8">
        <h2 className="font-serif-display text-2xl text-[#2E332C] mb-6">Receita — últimos 12 meses</h2>
        <div style={{ width: "100%", height: 288, minHeight: 240 }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8E4D9" vertical={false} />
              <XAxis dataKey="name" stroke="#5C6658" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#5C6658" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `R$${v}`} />
              <Tooltip
                cursor={{ fill: "#F1EEE3" }}
                contentStyle={{ background: "#FFFFFF", border: "1px solid #E8E4D9", borderRadius: 12 }}
                formatter={(v) => formatBRL(v)}
              />
              <Bar dataKey="total" fill="#C4A77D" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="elegant-card p-8">
          <h2 className="font-serif-display text-2xl text-[#2E332C] mb-6">Áreas mais tatuadas</h2>
          {(!data.body_parts || data.body_parts.length === 0) ? (
            <p className="text-sm text-[#5C6658]">Cadastre a parte do corpo nas tatuagens para ver o ranking.</p>
          ) : (
            <ul className="space-y-3" data-testid="body-parts-ranking">
              {data.body_parts.map((bp, i) => (
                <li key={bp.name + i} className="flex items-center gap-4">
                  <span className="font-serif-display text-lg w-6 text-[#7B8B6F]">{i + 1}</span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-[#2E332C] capitalize">{bp.name}</span>
                      <span className="text-xs text-[#5C6658]">{bp.count}</span>
                    </div>
                    <div className="h-2 bg-[#F1EEE3] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#7B8B6F] rounded-full transition-all"
                        style={{ width: `${(bp.count / maxBody) * 100}%` }}
                      />
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="elegant-card p-8">
          <h2 className="font-serif-display text-2xl text-[#2E332C] mb-6">Métodos de pagamento (ano)</h2>
          <ul className="space-y-4">
            {[
              ["pix", "Pix"], ["dinheiro", "Dinheiro"], ["cartao", "Cartão"], ["outro", "Outro"],
            ].map(([k, label]) => {
              const v = data.method_totals?.[k] || 0;
              const totalMethods = Object.values(data.method_totals || {}).reduce((a, b) => a + b, 0) || 1;
              const pct = (v / totalMethods) * 100;
              return (
                <li key={k}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-[#2E332C]">{label}</span>
                    <span className="text-[#5C6658]">{formatBRL(v)}</span>
                  </div>
                  <div className="h-2 bg-[#F1EEE3] rounded-full overflow-hidden">
                    <div className="h-full bg-[#C4A77D] rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </motion.div>
  );
}
